import { useEffect, useRef } from "react";
import { track } from "@/lib/analytics";

/**
 * Émet `section_viewed` une seule fois quand la section entre dans le viewport.
 * Sert à mesurer la progression de scroll (funnel "page 1 → 2 → … → pricing").
 * `order` = rang de la section (0 = tout en haut) pour ordonner le funnel dans PostHog.
 */
export default function TrackSection({
  name,
  order,
  children,
}: {
  name: string;
  order: number;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !fired.current) {
            fired.current = true;
            track("section_viewed", { section: name, order });
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.4 }, // 40% visible = "vue"
    );
    io.observe(el);
    return () => io.disconnect();
  }, [name, order]);

  return (
    <div ref={ref} data-section={name}>
      {children}
    </div>
  );
}
